import { routerMiddleware } from 'connected-react-router';
import { createBrowserHistory } from 'history';
import debounce from 'lodash-es/debounce';
import ReactGA from 'react-ga';
import { Tracker } from 'react-tracker';
import { applyMiddleware, compose, legacy_createStore as createStore } from 'redux';
import thunk from 'redux-thunk';
import trackListeners from '../infrastructure/middleware/analytics/listeners';
import trackingMiddleware from '../infrastructure/middleware/analytics/reduxTracking';
import { deleteState, loadState, saveState } from '../infrastructure/middleware/storage/reduxLocalStorage';
import { CLEAR_LOCAL_STORAGE_DEBOUNCE_TIMEOUT, WRITE_LOCAL_STORAGE_DEBOUNCE_TIMEOUT } from '../utilities/constants';
import { getResortLocationUrl } from '../utilities/lodgeData';
import createRootReducer from './reducers';

export const history = createBrowserHistory({
  basename: getResortLocationUrl()
});

// Set page on every router navigation, page view is triggered from GTM
history.listen(location => {
  ReactGA.set({ page: location.pathname });
});

// Set location and page on first load
ReactGA.set({
  location: window.location.href,
  page: window.location.pathname + window.location.search
});

const composeEnhancer =
  (window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ &&
    window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({
      trace: true, // Enabled tracing redux actions via redux devTools
      traceLimit: 25,
      actionSanitizer: action => (action.type?.startsWith('@@router/') ? { type: action.type } : action),
      stateSanitizer: state => {
        const { router, ...rest } = state; // Removed router related state from redux devTools
        return rest;
      }
    })) ||
  compose;

const tracker = new Tracker(trackListeners);

const middleware = [thunk, routerMiddleware(history), trackingMiddleware(tracker)];

// DEPRECATED: Following set of commented lines are slated to be removed after 30-JUN-2026.
// The 'preloadedStatesDict' object was previously used to reset specific keys on load.
// With inclusion-based persistence (inclusionList), fields not in the list
// are never saved, so they get their INITIAL_STATE from reducers on load.
// Kept commented for historical reference.
/*
const preloadedStatesDict = {
  'account.loginHasFailed': false,
  'account.loginErrorMessage': '',
  'account.creatingNewAccount': false,
  'account.newAccountHasSucceded': false,
  'account.newAccountValidationErrors': null,
  'account.newAccountErrorMessage': '',
  'plans.planAvailabilityApiStatus': API_STATUS.IDLE,
  'plans.recentSearchesApiStatus': API_STATUS.IDLE,
  'plans.deleteRecentSearchesApiStatus': API_STATUS.IDLE,
  'plans.filtersApplied': {},
  'plans.filtersApplied.themed': false,
  'plans.filtersApplied.standard': false,
  'plans.filtersApplied.premium': false,
  'plans.filtersApplied.accessibility': false,
  'plans.sorting': 'rec',
  'plans.sortSuite': '',
  'plans.oldFiltersApplied': {},
  'plans.requiresQualifyingID': false,
  'plans.qualifyingIDError': false,
  'plans.availabilityHighestRatesRequestFailed': false,
  'plans.availabilityRangeBySuiteApiStatus': API_STATUS.IDLE,
  'plans.isBookingWidgetFocused': false,
  'plans.isErrorModalAlreadyClosed': false,
  'plans.availabilityErrorMessage': '',
  'plans.offerCodeError': {},
  'plans.availabilityHighestRatesList': [],
  'plans.availabilityList': [],
  'plans.emailCampaign': {},
  'plans.offerType': '',
  'plans.personalizationResponses': [],
  'plans.promoDescription': '',
  'plans.qualifyingID': '',
  'plans.recentSearchesList': [],
  'plans.selectedSuiteForCalendar': '',
  'plans.selectedSuiteForRange': '',
  'packages.errorMessage': '',
  'packages.packagesApiStatus': API_STATUS.IDLE,
  'birthdayParty.summaryData': {},
  'suite.suiteAvailabilityApiStatus': API_STATUS.IDLE,
  'suite.suiteAvailabilityErrorMessage': '',
  'suite.responseHasPackages': false,
  'addApackage.addedPackages': {},
  'addApackage.flashMessage': '',
  'entities.deal.getErrorMessage': '',
  'globalModal.isVisible': false,
  'globalModal.keyContentComponent': undefined,
  'globalModal.shouldUseGWComponent': false,
  'globalModal.params': {},
  'profile.getProfileApiStatus': API_STATUS.IDLE,
  'profile.updateProfileApiStatus': API_STATUS.IDLE,
  'profile.user': {},
  'offer.description': false,
  'offer.errorMessage': false,
  reservations: {},
  'lodgeConfig.configFetchIsLoading': false,
  lodgesLocations: {}
};
*/

// Gets all keys from a slice, excluding specified keys.
const getAllSliceKeysExcept = (state, sliceName, excludedKeys = []) =>
  Object.keys(state[sliceName] ?? {})
    .filter(key => !excludedKeys.includes(key))
    .map(key => `${sliceName}.${key}`);

// Inclusion-based persistence list - introduced Q1 of 2026.
// Fields listed here WILL be persisted to localStorage
// See docs/redux-localstorage-unlisted-keys-analysis.md for full analysis
const getInclusionList = () => [
  // ===== birthdayParty =====
  'birthdayParty.birthdayAvailabilityData', // Set on search, used on PartyType/FoodAndDrinks/Payment. No party ID in URL.
  'birthdayParty.birthdayAvailabilityApiStatus', // Pairs with birthdayAvailabilityData. Prevents status/data mismatch.
  'birthdayParty.guestsOfHonor', // Set on FoodAndDrinks, used on Payment.
  'birthdayParty.numberOfExtraGuests', // Set on Search, used on ProtectedPartyFlow.
  'birthdayParty.partyAdults', // Set on Search, used on Payment.
  'birthdayParty.partyDate', // Set on Search, used on PartyType/Payment.
  'birthdayParty.partyGuestsCount', // Set on Search, used on PartyType/ProtectedPartyFlow.
  'birthdayParty.partyInfants', // Set on Search, used on Payment.
  'birthdayParty.partyKids', // Set on Search, used on Payment.
  'birthdayParty.partySpectators', // Set on Search, used on Payment.
  'birthdayParty.partyTime', // Set on Search, used on Payment.
  'birthdayParty.selectedParty', // Set on PartyType, used on FoodAndDrinks/AddOns/Payment.

  // ===== cart =====
  'cart.cartLastVisitedStep', // Tracks user progress for cart navigation.
  'cart.cartResortLocation', // User's selected resort for cart context.
  'cart.expiredCart', // UX messaging - shows "Expired" vs "Empty" cart message.

  // ===== currentLodge =====
  'currentLodge.resortLocation', // Set in AppConfiguration useEffect. Race condition: API calls can fire before useEffect runs.

  // ===== dates =====
  'dates.checkinDateSelection', // User input, used across 20+ files.
  'dates.checkoutDateSelection', // User input, used across 20+ files.

  // ===== dayPasses =====
  'dayPasses.arrivalDate', // User input for day passes.
  'dayPasses.availabilityKey', // Set on Search, used on Payment. F5 crash without it.
  'dayPasses.departureDate', // User input for season passes.
  'dayPasses.item', // Selected pass item data (rate/itemCode/rateCode).
  'dayPasses.numberOfGuests', // User input guest count.
  'dayPasses.parkingFee', // Calculated fee from API - shown on payment summary.
  'dayPasses.passType', // User selection (full/half day/season).
  'dayPasses.promoCode', // User input promo code.
  'dayPasses.promoCodeData', // Promo validation result from API - needed on payment.
  'dayPasses.qualifyingId', // User input qualifying ID for special rates.
  'dayPasses.resortFee', // Calculated fee from API - shown on payment summary.
  'dayPasses.seasonInfo', // API response for season pass payment.
  'dayPasses.seasonPassChargesApiStatus', // Pairs with fees data. Prevents status/data mismatch.
  'dayPasses.seasonPassInfoApiStatus', // Pairs with seasonInfo. Prevents status/data mismatch.
  'dayPasses.selectedDescription', // Display text for selected pass.
  'dayPasses.selectedItemCode', // Set on Search, used on Payment.
  'dayPasses.selectedRate', // Set on Search, used on Payment Summary.
  'dayPasses.selectedRateCode', // Set on Search, used on Payment.
  'dayPasses.selectedTitle', // Display title.
  'dayPasses.sustainabilityFee', // Calculated fee from API.
  'dayPasses.taxesAndFees', // Calculated taxes from API - shown on payment summary.
  'dayPasses.total', // Total from API - shown on payment.

  // ===== entities.manageReservationDetails =====
  'entities.manageReservationDetails.data', // Reservation data including ID. Required for confirmation page after booking.
  'entities.manageReservationDetails.packageErrorMessageData', // User-facing error message for packages.
  'entities.manageReservationDetails.errorMessage', // User-facing error message for reservation actions.
  'entities.manageReservationDetails.cabanaErrorMessage', // User-facing error message for cabana actions.

  // ===== flow =====
  'flow.flowSteps', // Navigation steps for DynamicFlow. Avoids loading flicker on F5.
  'flow.flowName', // Flow identifier used in DynamicFlow.js sync check.
  'flow.flowAudience', // Flow audience config.
  'flow.flowResortLocation', // Resort for current flow.
  'flow.flowLastUpdated', // Cache timestamp for 5-min TTL staleness check.

  // ===== guests =====
  'guests.adultsCount', // User input, used across 20+ files.
  'guests.kidsCount', // User input, used across 20+ files.
  'guests.kidsAges', // User input array of ages, sent to backend.

  // ===== offer =====
  'offer.code', // User input/selection, used on Plan/Payment.
  'offer.activeOffers', // Set on Plan, used on Payment.
  'offer.emailOffers', // Set on Plan, used on Payment.

  // ===== packages =====
  'packages.planPackages', // Set on Plan, used on Activities/Dining. NOT refetched.
  'packages.selectedPackages', // User selections on Activities/Dining.
  'packages.packageAvailabilityList', // Birthday flow: set on PartyType, used on FoodAndDrinks/AddOns. NOT refetched.

  // ===== plans =====
  'plans.upsellRecommendationData', // Set on Plan, used on Activities/Dining/Payment/analytics. NOT refetched.
  'plans.isAPScall', // Used by TimerModalContent across pages. Controls timer behavior.

  // ===== preCheckIn =====
  'preCheckIn.preCheckInStep', // User progress in pre-check-in flow.
  'preCheckIn.addedPackages', // User selections during pre-check-in.

  // ===== suite =====
  'suite.selectedSuite', // User selection. Used on Activities/Dining/Payment/Summary. F5 WILL CRASH.
  'suite.suiteDetailedAvailability', // Set on Plan, used on Payment/Summary/ProtectedBookingFlow.
  'suite.suiteLcoPackage', // Set on Plan, used on Activities.
  'suite.flexTripAllowed', // Set on Plan, used on Payment.
  'suite.flexPackage', // Set on Plan, used on Payment.
  'suite.dayType', // Set on Plan, used on Payment.
  'suite.personalizationResponses', // User input, used on Payment.
  'suite.taxesAndFeesDetailAvailability', // Set on Plan, used on Payment/Summary.

  // ===== lodgeConfig =====
  // All keys from state.lodgeConfig except 'configFetchIsLoading' (transient loading state not saved).
  // Safe: getInclusionList() is only called from triggerStateSave (subscribe), so store exists and
  // lodgeConfig has been normalized to full key set by the reducer's initializeState().
  ...getAllSliceKeysExcept(store.getState(), 'lodgeConfig', ['configFetchIsLoading'])
];

let persistedState = loadState();
export const localCreateStore = () => {
  const store = createStore(
    createRootReducer(history), // root reducer with router state
    persistedState,
    composeEnhancer(
      applyMiddleware(
        ...middleware
        // for dispatching history actions
        // ... other middlewares ...
      )
    )
  );

  return store;
};

export const triggerStateSave = () => {
  const state = store.getState();
  const { router, ...stateToPersist } = state; // Removed router related state from being saved to local storage
  saveState(stateToPersist, getInclusionList());
};

export const store = localCreateStore();

store.subscribe(
  debounce(() => {
    deleteState();
    console.log('Storage deleted');
  }, CLEAR_LOCAL_STORAGE_DEBOUNCE_TIMEOUT)
);

store.subscribe(
  debounce(() => {
    triggerStateSave();
  }, WRITE_LOCAL_STORAGE_DEBOUNCE_TIMEOUT)
);
